import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const textVariants = cva("", {
  variants: {
    variant: {
      h1: "scroll-m-20 text-3xl font-extrabold tracking-tight md:text-4xl lg:text-5xl",
      h2: "scroll-m-20 border-b pb-2 text-2xl font-semibold tracking-tight md:text-3xl first:mt-0",
      h3: "scroll-m-20 text-xl font-semibold tracking-tight md:text-2xl",
      h4: "scroll-m-20 text-lg font-semibold tracking-tight md:text-xl",
      p: "leading-7 [&:not(:first-child)]:mt-6",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      lead: "text-lg text-muted-foreground md:text-xl",
      large: "text-base font-semibold md:text-lg",
      small: "text-sm font-medium leading-none",
      muted: "text-sm text-muted-foreground",
      code: "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
    },
  },
  defaultVariants: {
    variant: "p",
  },
});

type TextElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "p"
  | "span"
  | "blockquote"
  | "code";

const variantElementMap: Record<
  NonNullable<VariantProps<typeof textVariants>["variant"]>,
  TextElement
> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  p: "p",
  blockquote: "blockquote",
  lead: "p",
  large: "p",
  small: "p",
  muted: "p",
  code: "code",
};

interface TextProps
  extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof textVariants> {
  asChild?: boolean;
  as?: TextElement;
}

function Text({
  className,
  variant = "p",
  asChild = false,
  as,
  ...props
}: TextProps) {
  const Comp = asChild ? Slot : as || variantElementMap[variant!];

  return (
    <Comp className={cn(textVariants({ variant, className }))} {...props} />
  );
}

export { Text, textVariants, type TextProps };
